import CelebrationIcon from '@mui/icons-material/Celebration';
import { Dialog, Stack, Typography } from '@mui/material';
import { ProductGroups } from 'enums';
import { Funnel } from 'features/co-pilot/co-pilot.types';
import PaiListItem from 'features/ui/base-components/pai-list-item';
import PaiButton from 'features/ui/base-components/pai-button';
import PaiLink from 'features/ui/base-components/pai-link';
import _ from 'lodash';
import React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import { currentlyInWorkTime, getNextFormatWorkTime } from 'utils/date.utils';
import Countdown from './countdown';
import getLocalizedText from 'features/co-pilot/translationFileProduct';
import { LinkedinAccount } from 'features/co-pilot/linkedin-accounts/linkedin-accounts.types';

interface Props {
  open: boolean;
  userFirstName: string;
  campaign: Funnel | null;
  linkedinAccount: LinkedinAccount | null;
  productGroup: ProductGroups;

  handleOpenStartImmediatelyDialog(): void;

  handleCloseAndProceed(): void;
}

const StartMessageApprovalCampaignSuccessDialog: React.FC<Props> = ({
  open,
  userFirstName,
  campaign,
  linkedinAccount,
  productGroup,
  handleOpenStartImmediatelyDialog,
  handleCloseAndProceed,
}) => {
  const { classes } = useStyles();
  const { t } = useTranslation();

  const campaignName = campaign?.name || '';
  const workDays = linkedinAccount?.workDays || [];
  const workHours = linkedinAccount?.workHours || [];
  const timeZone = linkedinAccount?.timeZone || 'Europe/Berlin';

  const nextWorkTime = !_.isEmpty(workDays) && !_.isEmpty(workHours) ? getNextFormatWorkTime(new Date(), workDays, workHours, timeZone) : undefined;

  const getLocalizedKey = (key: string) => {
    if (productGroup === ProductGroups.LEAD_BOOSTER) {
      return `${key}_LeadBooster`;
    }
    return `${key}_ActiveSourcing`;
  };

  return (
    <Dialog open={open} classes={{ paper: classes.paper }} fullWidth={true} maxWidth={'lg'}>
      <Stack direction={'row'}>
        <Stack className={classes.leftRow} gap={4}>
          <div>
            <CelebrationIcon className={classes.icon} />
            <Typography className={classes.boldText} mb={'16px'}>
              {t('congratulationsFirstName', { userFirstName })}
            </Typography>
            <Typography className={classes.text} mb={'4px'}>
              <Trans i18nKey={getLocalizedKey('campaignCreated')} values={{ campaignName }}>
                You have successfully created your recruiting outreach campaign <strong>{campaignName}</strong>.
              </Trans>
            </Typography>
            <Typography className={classes.text}>{getLocalizedText('boostConversionsInfo', productGroup, t)}</Typography>
          </div>
          <div>
            <Typography className={classes.boldText} mb={'16px'}>
              {t('keepTrackOf')}
            </Typography>
            <PaiListItem text={t('campaignStatusInfo')} color={'#FFF'} size={'extra-large'} />
            <PaiListItem text={t('requiredActionsInfo')} color={'#FFF'} size={'extra-large'} />
            <PaiListItem text={t('editCampaignInfo')} color={'#FFF'} size={'extra-large'} />
          </div>
        </Stack>
        <Stack className={classes.rightRow}>
          <Stack justifyContent={'center'} gap={4} sx={{ height: 'calc(100% - 77px)', padding: '40px' }}>
            <div>
              <Typography className={classes.regularText} mb={'8px'}>
                {t('campaignEstimatedStart')}
              </Typography>
              <Typography className={classes.regularText} fontWeight={'bold'}>
                {!_.isNil(nextWorkTime) ? nextWorkTime : null}
              </Typography>
            </div>
            {!currentlyInWorkTime(new Date(), workDays, workHours) ? (
              <>
                <Countdown workDays={workDays} workHours={workHours} timeZone={timeZone} />
                <PaiLink
                  text={t('startCampaignSkipWorkTimeCheck')}
                  size={'lg'}
                  variant={'primary'}
                  underline={'always'}
                  action={handleOpenStartImmediatelyDialog}
                />
              </>
            ) : null}
          </Stack>
          <div className={classes.actions}>
            <PaiButton text={t('continueToStatus')} size={'small'} border={'rounded'} action={handleCloseAndProceed} />
          </div>
        </Stack>
      </Stack>
    </Dialog>
  );
};

const useStyles = makeStyles()((theme) => ({
  paper: {
    borderRadius: '16px',
  },
  boldText: {
    fontSize: theme.typography.pxToRem(30),
    fontWeight: 'bold',
  },
  text: {
    fontSize: theme.typography.pxToRem(15),
  },
  leftRow: {
    flexBasis: '50%',
    background: 'linear-gradient(to right, #4AA4FF, #5044FF)',
    borderTopRightRadius: '16px',
    borderBottomRightRadius: '16px',
    color: '#FFF',
    padding: '40px 72px',
  },
  icon: {
    fontSize: theme.typography.pxToRem(80),
  },
  rightRow: {
    flexBasis: '50%',
  },
  regularText: {
    fontSize: theme.typography.pxToRem(16),
  },
  actions: {
    display: 'flex',
    justifyContent: 'flex-end',
    padding: '24px',
    marginTop: 'auto',
    borderTop: `1px solid ${theme.palette.greys.light}`,
  },
}));

export default StartMessageApprovalCampaignSuccessDialog;
